import { useCopyToClipboard } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { User } from '@consigli/types';
import { MdCheck, MdContentCopy } from 'react-icons/md';

import { errorToast } from '@/components/toast';

/**
 * Copies the user's core-API id. It is already reachable under Actions → View
 * details, but as one field of a JSON dump whose copy button takes the whole
 * object. This is one click, and just the id.
 *
 * The id is not rendered here: it is only ever wanted in a clipboard.
 *
 * A component, not an inline cell, so each row holds its own hook — `copied` is
 * one boolean per instance, and a shared one would light up every row.
 */
export const UserIdCell = ({ user }: { user: User }) => {
  const { copied, copyToClipboard } = useCopyToClipboard();

  const handleCopy = async () => {
    // The hook returns false and swallows the reason, usually a non-secure
    // context. Silently doing nothing is worse than saying so.
    const copiedOk = await copyToClipboard(user.id);
    if (!copiedOk) {
      errorToast({ message: 'Could not copy the user ID' });
    }
  };

  // Some records carry neither a name nor an email — the Name column renders a
  // bare space for those. Falling back keeps the label from becoming
  // "Copy user ID for  ", which tells a screen reader nothing.
  const who = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email;

  return (
    <>
      <Button
        // shadcn's Button spreads onto a bare <button> with no default type, so
        // without this it is implicitly a submit.
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
        aria-label={who ? `Copy user ID for ${who}` : 'Copy user ID'}
        title={copied ? 'Copied!' : 'Copy user ID'}
      >
        {copied ? (
          <MdCheck className="h-4 w-4 text-green-600" />
        ) : (
          <MdContentCopy className="h-4 w-4 text-gray-600" />
        )}
      </Button>
      {/* The icon swap and title are the only success signal otherwise, and
          neither reaches a screen reader — nor does the id, since it is not
          rendered. */}
      <span className="sr-only" role="status">
        {copied ? (who ? `User ID for ${who} copied` : 'User ID copied') : ''}
      </span>
    </>
  );
};
